import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, Observable, shareReplay, tap, throwError } from 'rxjs';
import { decodeJwtPayload } from '@clinic-platform/shared-types';

import { ApiClientService } from '../http/api-client.service';
import {
  AcceptClientInviteRequest,
  AcceptClientInviteResponse,
  AcceptUserInviteRequest,
  AcceptUserInviteResponse,
  ClientEmailVerificationRequest,
  ClientEmailVerificationResponse,
  ClientInviteMetaResponse,
  ClientRegistrationRequest,
  ClientRegistrationResponse,
  JwtPayload,
  LoginRequest,
  LoginResponse,
  LoginSuccessResponse,
  UserInviteMetaResponse,
  TwoFactorEnableResponse,
  TwoFactorPolicyResponse,
  TwoFactorSetupResponse,
  TwoFactorStatusResponse
} from './auth.models';
import { TokenStorageService } from './token-storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService
{
  private readonly apiClient = inject(ApiClientService);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly router = inject(Router);
  private readonly preferredCompanySlugKey = 'clinic_preferred_company_slug';
  private readonly trustedDeviceTokenKey = 'clinic_trusted_device_token';
  private twoFactorStatusRequest$: Observable<TwoFactorStatusResponse> | null = null;

  public login(payload: LoginRequest): Observable<LoginResponse>
  {
    const headers: Record<string, string> = {};

    if (payload.companyId?.trim())
    {
      headers['x-company-id'] = payload.companyId.trim();
    }

    if (payload.companySlug?.trim())
    {
      headers['x-company-slug'] = payload.companySlug.trim().toLowerCase();
    }

    const loginPath = headers['x-company-id'] || headers['x-company-slug']
      ? '/auth/login'
      : '/auth/login-global';

    return this.apiClient.post<LoginResponse, {
      email: string;
      password: string;
      totpCode?: string;
      trustedDeviceToken?: string;
      trustDevice?: boolean;
    }>(
      loginPath,
      {
        email: payload.email,
        password: payload.password,
        totpCode: payload.totpCode,
        trustedDeviceToken: payload.trustedDeviceToken,
        trustDevice: payload.trustDevice,
      },
      undefined,
      headers
    ).pipe(
      tap((response: LoginResponse) =>
      {
        if (!this.isLoginSuccessResponse(response))
        {
          return;
        }

        this.tokenStorage.setAccessToken(response.accessToken);
        this.invalidateTwoFactorStatusCache();

        if (response.trustedDeviceToken)
        {
          this.setTrustedDeviceToken(response.trustedDeviceToken);
        }

        if (payload.companySlug?.trim())
        {
          this.setPreferredCompanySlug(payload.companySlug);
        }
      })
    );
  }

  public registerClient(payload: ClientRegistrationRequest): Observable<ClientRegistrationResponse>
  {
    const companySlug = payload.companySlug.trim().toLowerCase();
    const headers: Record<string, string> = {
      'x-company-slug': companySlug,
    };

    return this.apiClient.post<ClientRegistrationResponse, { email: string; password: string }>(
      '/auth/register-client',
      {
        email: payload.email.trim(),
        password: payload.password,
      },
      undefined,
      headers,
    );
  }

  public verifyClientEmail(payload: ClientEmailVerificationRequest): Observable<ClientEmailVerificationResponse>
  {
    const companySlug = payload.companySlug.trim().toLowerCase();
    const headers: Record<string, string> = {
      'x-company-slug': companySlug,
    };

    return this.apiClient.post<ClientEmailVerificationResponse, { token: string }>(
      '/auth/register-client/verify',
      {
        token: payload.token.trim(),
      },
      undefined,
      headers,
    );
  }

  public getClientInviteMeta(token: string): Observable<ClientInviteMetaResponse>
  {
    return this.apiClient.get<ClientInviteMetaResponse>(
      `/auth/client-invites/${encodeURIComponent(token.trim())}/meta`,
    );
  }

  public acceptClientInvite(payload: AcceptClientInviteRequest): Observable<AcceptClientInviteResponse>
  {
    return this.apiClient.post<AcceptClientInviteResponse, AcceptClientInviteRequest>(
      '/auth/client-invites/accept',
      {
        token: payload.token.trim(),
        password: payload.password,
      },
    );
  }

  public getUserInviteMeta(token: string): Observable<UserInviteMetaResponse>
  {
    return this.apiClient.get<UserInviteMetaResponse>(
      `/auth/user-invites/${encodeURIComponent(token.trim())}/meta`,
    );
  }

  public acceptUserInvite(payload: AcceptUserInviteRequest): Observable<AcceptUserInviteResponse>
  {
    return this.apiClient.post<AcceptUserInviteResponse, AcceptUserInviteRequest>(
      '/auth/user-invites/accept',
      {
        token: payload.token.trim(),
        password: payload.password,
      },
    );
  }

  public getTwoFactorStatus(): Observable<TwoFactorStatusResponse>
  {
    if (!this.twoFactorStatusRequest$) {
      this.twoFactorStatusRequest$ = this.apiClient.get<TwoFactorStatusResponse>('/auth/2fa/status').pipe(
        catchError((error: unknown) => {
          this.invalidateTwoFactorStatusCache();
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.twoFactorStatusRequest$;
  }

  public setupTwoFactor(): Observable<TwoFactorSetupResponse>
  {
    return this.apiClient.post<TwoFactorSetupResponse>('/auth/2fa/setup', {});
  }

  public enableTwoFactor(code: string): Observable<TwoFactorEnableResponse>
  {
    return this.apiClient.post<TwoFactorEnableResponse, { code: string }>(
      '/auth/2fa/enable',
      { code: code.trim() },
    ).pipe(
      tap(() => this.invalidateTwoFactorStatusCache()),
    );
  }

  public disableTwoFactor(code: string): Observable<{ enabled: boolean }>
  {
    return this.apiClient.post<{ enabled: boolean }, { code: string }>(
      '/auth/2fa/disable',
      { code: code.trim() },
    ).pipe(
      tap(() => this.invalidateTwoFactorStatusCache()),
    );
  }

  public getTwoFactorPolicy(companyId?: string): Observable<TwoFactorPolicyResponse>
  {
    const query = companyId ? { companyId } : undefined;
    return this.apiClient.get<TwoFactorPolicyResponse>('/auth/2fa/policy', query);
  }

  public updateTwoFactorPolicy(
    mandatoryRoles: string[],
    useInherited = false,
    companyId?: string,
  ): Observable<TwoFactorPolicyResponse>
  {
    const query = companyId ? { companyId } : undefined;
    return this.apiClient.post<
      TwoFactorPolicyResponse,
      { mandatoryRoles: string[]; useInherited: boolean }
    >(
      '/auth/2fa/policy',
      { mandatoryRoles, useInherited },
      query,
    );
  }

  public logout(): void
  {
    const preferredSlug = this.getPreferredCompanySlug();

    this.invalidateTwoFactorStatusCache();
    this.tokenStorage.clear();

    if (preferredSlug)
    {
      void this.router.navigate(['/', preferredSlug, 'login']);
      return;
    }

    void this.router.navigate(['/login']);
  }

  public getAccessToken(): string | null
  {
    return this.tokenStorage.getAccessToken();
  }

  public isAuthenticated(): boolean
  {
    return !!this.getAccessToken();
  }

  public getCurrentUserEmail(): string | null
  {
    return this.getJwtPayload()?.email ?? null;
  }

  public getCurrentRole(): string | null
  {
    return this.getJwtPayload()?.role ?? null;
  }

  public getCurrentCompanyId(): string | null
  {
    return this.getJwtPayload()?.companyId ?? null;
  }

  public getPreferredCompanySlug(): string | null
  {
    return localStorage.getItem(this.preferredCompanySlugKey);
  }

  public setPreferredCompanySlug(slug: string): void
  {
    const normalized = slug.trim().toLowerCase();

    if (!normalized)
    {
      return;
    }

    localStorage.setItem(this.preferredCompanySlugKey, normalized);
  }

  public clearPreferredCompanySlug(): void
  {
    localStorage.removeItem(this.preferredCompanySlugKey);
  }

  public getTrustedDeviceToken(): string | null
  {
    return localStorage.getItem(this.trustedDeviceTokenKey);
  }

  public setTrustedDeviceToken(token: string): void
  {
    const normalized = token.trim();
    if (!normalized)
    {
      return;
    }
    localStorage.setItem(this.trustedDeviceTokenKey, normalized);
  }

  public clearTrustedDeviceToken(): void
  {
    localStorage.removeItem(this.trustedDeviceTokenKey);
  }

  private invalidateTwoFactorStatusCache(): void
  {
    this.twoFactorStatusRequest$ = null;
  }

  private getJwtPayload(): JwtPayload | null
  {
    return decodeJwtPayload<JwtPayload>(this.getAccessToken());
  }

  private isLoginSuccessResponse(response: LoginResponse): response is LoginSuccessResponse
  {
    return !!(response as LoginSuccessResponse).accessToken;
  }
}
